import { useState, useEffect } from 'react';

const SITE_NAV_ID = 'site-navigation';

function readStickyTopPx() {
  if (typeof window === 'undefined') return 0;

  let top = 0;

  const visualViewport = window.visualViewport;
  if (visualViewport) {
    top = Math.max(top, visualViewport.offsetTop);
  }

  const nav = document.getElementById(SITE_NAV_ID);
  if (!nav) return Math.round(top);

  const rect = nav.getBoundingClientRect();
  const position = window.getComputedStyle(nav).position;
  const navPinned = position === 'sticky' || position === 'fixed';

  if (navPinned) {
    top = Math.max(top, rect.bottom);
  } else if (rect.bottom > 0) {
    top = Math.max(top, rect.bottom);
  }

  return Math.max(Math.round(top), 0);
}

/**
 * Offset for position:sticky calendar headers — below site toolbar and
 * mobile browser chrome (updates on scroll/resize/visualViewport).
 */
export function useCalendarStickyTop() {
  const [stickyTopPx, setStickyTopPx] = useState(0);

  useEffect(() => {
    let rafId = 0;

    const update = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setStickyTopPx(readStickyTopPx());
      });
    };

    update();

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    const nav = document.getElementById(SITE_NAV_ID);
    const resizeObserver =
      nav && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(update)
        : null;
    resizeObserver?.observe(nav);

    return () => {
      cancelAnimationFrame(rafId);
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
    };
  }, []);

  return stickyTopPx;
}

export default useCalendarStickyTop;
